// src/components/FaqSection.tsx
import { useState, useRef } from 'react';
import {
  motion,
  AnimatePresence,
  useInView,
  type Variants as MotionVariants,
} from 'framer-motion';
import { FaqItem } from '@/data/serviceFaqs';
import { cn } from '@/lib/utils';

interface FaqSectionProps {
  title?: string;
  description?: string;
  faqs: FaqItem[];
  bgColor?: string;
  hideHeader?: boolean;
}

const FaqSection = ({
  title = 'Frequently Asked Questions',
  description,
  faqs,
  bgColor = 'bg-gray-50',
  hideHeader = false,
}: FaqSectionProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const toggleItem = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  // Split the FAQs into two columns for desktop view
  const halfwayIndex = Math.ceil(faqs.length / 2);
  const firstColumnFaqs = faqs.slice(0, halfwayIndex);
  const secondColumnFaqs = faqs.slice(halfwayIndex);

  return (
    <section id="faq" className={`py-0 ${bgColor}`} ref={ref}>
      <div className={hideHeader ? '' : 'container mx-auto px-4'}>
        {!hideHeader && (
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}>
              {title}
            </motion.h2>
            <motion.div
              className="h-1 w-24 bg-secondary mx-auto"
              initial={{ width: 0 }}
              animate={isInView ? { width: 96 } : { width: 0 }}
              transition={{ duration: 0.7 }}
            />

            {description && (
              <motion.p
                className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={
                  isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
                }
                transition={{ duration: 0.5, delay: 0.2 }}>
                {description}
              </motion.p>
            )}
          </div>
        )}

        {/* Mobile view - single column */}
        <div className="md:hidden space-y-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="space-y-4">
            {faqs.map((item, index) => (
              <FaqItemComponent
                key={index}
                item={item}
                index={index}
                activeIndex={activeIndex}
                toggleItem={toggleItem}
                variants={itemVariants}
              />
            ))}
          </motion.div>
        </div>

        {/* Desktop view - two columns */}
        <div className="hidden md:grid md:grid-cols-2 gap-6">
          {/* First Column */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="space-y-4">
            {firstColumnFaqs.map((item, index) => (
              <FaqItemComponent
                key={index}
                item={item}
                index={index}
                activeIndex={activeIndex}
                toggleItem={toggleItem}
                variants={itemVariants}
              />
            ))}
          </motion.div>

          {/* Second Column */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="space-y-4">
            {secondColumnFaqs.map((item, index) => (
              <FaqItemComponent
                key={index + halfwayIndex}
                item={item}
                index={index + halfwayIndex}
                activeIndex={activeIndex}
                toggleItem={toggleItem}
                variants={itemVariants}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Extract the FAQ item into a reusable component
interface FaqItemComponentProps {
  item: FaqItem;
  index: number;
  activeIndex: number | null;
  toggleItem: (index: number) => void;
  variants: MotionVariants;
}

const FaqItemComponent = ({
  item,
  index,
  activeIndex,
  toggleItem,
  variants,
}: FaqItemComponentProps) => {
  return (
    <motion.div
      className="border border-gray-200 rounded-lg overflow-hidden"
      variants={variants}>
      <button
        className="flex justify-between items-center w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
        onClick={() => toggleItem(index)}>
        <span className="text-xl font-semibold">{item.question}</span>
        <svg
          className={cn(
            'w-6 h-6 transform transition-transform',
            activeIndex === index ? 'rotate-180' : '',
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      <AnimatePresence>
        {activeIndex === index && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden">
            <div className="px-6 py-4 bg-gray-50">
              <p className="text-lg">{item.answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FaqSection;
